import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanEditor } from '../editor';
import { describeDrop, zoneAtOffset } from '../editor/dropFeedback';
import type { DropOutcome } from '../editor/dropFeedback';
import { subtreeHeight } from '../editor/treeOps';
import { useDragAutoScroll } from '../useDragAutoScroll';

import { DRAG_THRESHOLD_PX } from './timelineGeometry';

/**
 * Restructure drags in the timeline: reorder and reparent by dragging a row's
 * name cell. Depth never comes from X — that axis belongs to `useBarDrag` and
 * means time. See ADR 0019.
 *
 * Everything mutable lives in one ref session, so a pointer move costs a hit
 * test, two map lookups and a pure resolve. The dragged subtree's height and
 * visible run are computed once on pointerdown — recomputing them per frame
 * would walk the subtree on every move (BR-3).
 */

export type RowDragFeedback = {
  outcome: DropOutcome;
  /** Row index the drop line sits above; null when nothing is drawn. */
  gapIndex: number | null;
  /** Row index of the prospective parent, when it is visible. */
  parentIndex: number | null;
  /** Depth the moved node would land at, for the line's indent. */
  depth: number;
};

export type RowDragState = {
  nodeId: string;
  /** Visible row range of the travelling block, inclusive. */
  fromIndex: number;
  toIndex: number;
};

export type RowDragApi = {
  drag: RowDragState | null;
  feedback: RowDragFeedback | null;
  justMovedId: string | null;
  namePointerDown: (nodeId: string) => (event: ReactPointerEvent<HTMLElement>) => void;
  cardRef: RefObject<HTMLDivElement | null>;
};

type Session = {
  nodeId: string;
  originX: number;
  originY: number;
  lastClientX: number;
  lastClientY: number;
  moved: boolean;
  fromIndex: number;
  toIndex: number;
  dragHeight: number;
  /** Arrow-key adjustment, null until the user presses one. */
  depthOverride: number | null;
  /** Last resolved outcome, committed on release. */
  outcome: DropOutcome | null;
  /** Dedupe key so feedback state changes ~10x/sec, not per move. */
  lastKey: string;
};

export const useRowDrag = (scrollElement: HTMLElement | null): RowDragApi => {
  const { t } = useTranslation('pmPlanning');
  const { state, dispatch, flat } = usePlanEditor();
  const [drag, setDrag] = useState<RowDragState | null>(null);
  const [feedback, setFeedback] = useState<RowDragFeedback | null>(null);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);

  const session = useRef<Session | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Window listeners are bound once per drag but must see the current tree.
  const planRef = useRef(state.document.plan);
  const flatRef = useRef(flat);
  const foldedRef = useRef(state.foldedIds);
  // The readout's words; a ref like the rest, so a language switch mid-drag is seen.
  const tRef = useRef(t);
  useEffect(() => {
    planRef.current = state.document.plan;
    flatRef.current = flat;
    foldedRef.current = state.foldedIds;
    tRef.current = t;
  });

  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const clearFeedback = useCallback(() => {
    const current = session.current;
    if (!current) return;
    current.outcome = null;
    // Clearing on EVERY early return is what stops a release outside the rows
    // from committing the last target seen — the bug the plan tree had.
    if (current.lastKey !== '') {
      current.lastKey = '';
      setFeedback(null);
    }
  }, []);

  /** Resolve from the last known pointer position. Also used after each scroll. */
  const resolveAt = useCallback(() => {
    const current = session.current;
    if (!current || !current.moved) return;

    const element = document
      .elementFromPoint(current.lastClientX, current.lastClientY)
      ?.closest<HTMLElement>('[data-node-id]');
    const nodeId = element?.dataset.nodeId;
    if (!element || !nodeId) {
      clearFeedback();
      return;
    }
    const targetIndex = flatRef.current.index.rowIndexById.get(nodeId);
    if (targetIndex === undefined) {
      clearFeedback();
      return;
    }

    const rect = element.getBoundingClientRect();
    const zone = zoneAtOffset(current.lastClientY - rect.top);

    const outcome = describeDrop({
      plan: planRef.current,
      flat: flatRef.current,
      dragId: current.nodeId,
      dragIndex: current.fromIndex,
      dragEndIndex: current.toIndex,
      dragHeight: current.dragHeight,
      targetIndex,
      zone,
      depthOverride: current.depthOverride,
      foldedIds: foldedRef.current,
      t: tRef.current,
    });

    current.outcome = outcome;

    const gapIndex = outcome.kind === 'move' ? outcome.resolution.depth : -1;
    const key = `${String(targetIndex)}|${zone}|${outcome.kind}|${String(gapIndex)}|${
      outcome.kind === 'move' ? outcome.message : outcome.message
    }`;
    if (key === current.lastKey) return;
    current.lastKey = key;

    if (outcome.kind !== 'move') {
      setFeedback({ outcome, gapIndex: null, parentIndex: null, depth: 0 });
      return;
    }

    const parentIndex =
      outcome.parentId === null
        ? null
        : (flatRef.current.index.rowIndexById.get(outcome.parentId) ?? null);

    setFeedback({
      outcome,
      gapIndex: gapIndexFor(flatRef.current, outcome.resolution, targetIndex, zone),
      parentIndex,
      depth: outcome.resolution.depth,
    });
  }, [clearFeedback]);

  const autoScroll = useDragAutoScroll(scrollElement, resolveAt);

  const namePointerDown = useCallback(
    (nodeId: string) => (event: ReactPointerEvent<HTMLElement>) => {
      // The row root selects on pointerdown; this gesture owns the name cell.
      event.stopPropagation();
      event.preventDefault();

      const rows = flatRef.current.rows;
      const fromIndex = flatRef.current.index.rowIndexById.get(nodeId);
      const node = flatRef.current.index.nodeById.get(nodeId);
      if (fromIndex === undefined || !node) return;

      const descendants = flatRef.current.index.descendantCountById.get(nodeId) ?? 0;
      session.current = {
        nodeId,
        originX: event.clientX,
        originY: event.clientY,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
        moved: false,
        fromIndex,
        // Descendants hidden by a fold are not visible rows, so the block ends
        // at the last row that is actually on screen.
        toIndex: Math.min(fromIndex + descendants, rows.length - 1),
        dragHeight: subtreeHeight(node),
        depthOverride: null,
        outcome: null,
        lastKey: '',
      };

      const finish = (commit: boolean) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        window.removeEventListener('keydown', onKeyDown);
        autoScroll.stop();
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        const current = session.current;
        session.current = null;
        setDrag(null);
        setFeedback(null);
        if (!current) return;

        if (!current.moved) {
          // Never crossed the threshold: this was a click.
          if (state.activeId !== current.nodeId) {
            dispatch({ type: 'SET_ACTIVE', id: current.nodeId });
          }
          return;
        }
        if (!commit || current.outcome?.kind !== 'move') return;

        const { resolution, unfoldParentId } = current.outcome;
        dispatch({ type: 'MOVE_SUBTREE', sourceId: current.nodeId, target: resolution });
        // Otherwise the node lands inside a fold and appears to vanish.
        if (unfoldParentId) dispatch({ type: 'TOGGLE_FOLD', id: unfoldParentId });
        dispatch({ type: 'SET_ACTIVE', id: current.nodeId });

        setJustMovedId(current.nodeId);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setJustMovedId(null), 900);
      };

      const onMove = (moveEvent: PointerEvent) => {
        const current = session.current;
        if (!current) return;
        current.lastClientX = moveEvent.clientX;
        current.lastClientY = moveEvent.clientY;

        if (!current.moved) {
          const dx = moveEvent.clientX - current.originX;
          const dy = moveEvent.clientY - current.originY;
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
          current.moved = true;
          document.body.style.userSelect = 'none';
          document.body.style.cursor = 'grabbing';
          setDrag({
            nodeId: current.nodeId,
            fromIndex: current.fromIndex,
            toIndex: current.toIndex,
          });
        }

        // The card follows imperatively — a React render per move would be
        // wasted work at pointer rate.
        const card = cardRef.current;
        if (card) {
          const flipX = moveEvent.clientX > window.innerWidth - 360;
          const flipY = moveEvent.clientY > window.innerHeight - 120;
          card.style.transform = `translate3d(${String(
            moveEvent.clientX + (flipX ? -340 : 14),
          )}px, ${String(moveEvent.clientY + (flipY ? -110 : 18))}px, 0)`;
        }

        autoScroll.track(moveEvent.clientY);
        resolveAt();
      };

      const onUp = () => finish(true);
      const onCancel = () => finish(false);
      const onKeyDown = (keyEvent: KeyboardEvent) => {
        const current = session.current;
        if (!current) return;
        if (keyEvent.key === 'Escape') {
          keyEvent.preventDefault();
          finish(false);
          return;
        }
        if (keyEvent.key !== 'ArrowLeft' && keyEvent.key !== 'ArrowRight') return;
        if (current.outcome?.kind !== 'move') return;
        keyEvent.preventDefault();
        const { range, resolution } = current.outcome;
        const next = resolution.depth + (keyEvent.key === 'ArrowRight' ? 1 : -1);
        current.depthOverride = Math.max(range.min, Math.min(range.max, next));
        // Force a re-resolve even though the pointer has not moved.
        current.lastKey = '';
        resolveAt();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('keydown', onKeyDown);
    },
    [autoScroll, dispatch, resolveAt, state.activeId],
  );

  return { drag, feedback, justMovedId, namePointerDown, cardRef };
};

/**
 * Which row boundary the drop line sits on. The resolution names a parent and a
 * child index; the line belongs above the row that would follow the insertion,
 * which for a first-child drop is the parent's own next visible row.
 */
const gapIndexFor = (
  flat: ReturnType<typeof usePlanEditor>['flat'],
  resolution: { parentId: string | null; childIndex: number },
  targetIndex: number,
  zone: 'before' | 'into' | 'after',
): number => {
  const siblings =
    resolution.parentId === null
      ? null
      : (flat.index.nodeById.get(resolution.parentId)?.children ?? null);
  const anchor = siblings?.[resolution.childIndex];
  if (anchor) {
    const index = flat.index.rowIndexById.get(anchor.id);
    if (index !== undefined) return index;
  }
  return zone === 'before' ? targetIndex : targetIndex + 1;
};
